import axios from 'axios';
import { isTokenExpired } from '../utils/auth';

class OdooService {
    constructor() {
        this.baseUrl = process.env.REACT_APP_ODOO_APP_URL
        this.sessionInfo = null;
        this.userId = null;
        this.loadSession();
    }

    loadSession() {
        const saved = localStorage.getItem('odoo_session');
        if (saved) {
            this.sessionInfo = JSON.parse(saved);
        }
    }

    saveSession(data) {
        this.sessionInfo = data;
        localStorage.setItem('odoo_session', JSON.stringify(data));
    }

    clearSession() {
        this.sessionInfo = null;
        localStorage.removeItem('odoo_session');
    }

    getConfig() {
        if (!this.sessionInfo || !this.sessionInfo.token) {
            throw new Error('No hay sesión activa');
        }

        if (isTokenExpired(this.sessionInfo.token)) {
            this.clearSession();
            throw new Error('Sesión expirada');
        }

        return {
            timeout: 10000,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.sessionInfo.token}`,
            }
        };
    }

    async login(username, password) {
        try {
            const response = await axios.post(`${this.baseUrl}/api/auth/login`, 
                {
                    jsonrpc: "2.0",
                    method: "call",
                    params: {
                        login: username,
                        password: password
                    }
                }, 
                {
                    timeout: 10000,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            const data = response.data.result;

            if (data && data.user_id && data.token) {
                const sessionData = {
                    userId: data.user_id,
                    token: data.token,
                    username: data.name,
                    loginTime: new Date().toISOString()
                };
            this.saveSession(sessionData)
            return { success: true, user: this.sessionInfo };
            } else {
                return {
                    success: false,
                    message: data.message
                }
            }
        } catch (error) {
            if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
                throw new Error('No se puede conectar al servidor Odoo. Verifica la URL y conexión.');
            } else if (error.response) {
                if (error.response.status === 401) {
                throw new Error('Usuario o contraseña incorrectos');
                } else if (error.response.status === 404) {
                throw new Error('Servidor Odoo no encontrado. Verifica la URL.');
                } else {
                throw new Error(`Error del servidor Odoo: ${error.response.status}`);
                }
            } else {
                throw new Error('Error de conexión con Odoo');
            }
        }
    }

    async validateLocationOrPackage(barcode) {
        try {
            const response = await axios.post(
                `${this.baseUrl}/api/stock/validate-location-or-package`,
                {
                    jsonrpc: "2.0",
                    method: "call",
                    params: {
                        barcode: barcode
                    }
                },this.getConfig()
            );

            const result = response.data.result

            if (result.success)
            {
                if (result.type === 'location') {
                    return {
                        success: true,
                        type: 'location',
                        location_id: result.data.id,
                        location_name: result.data.name
                    }   
                }

                if (result.type === 'package') {
                    return {
                        success: true,
                        type: 'package',
                        location_id: result.data.location_id,
                        location_name: result.data.location_name,
                        package_id: result.data.package_id,
                        package_name: result.data.package_name
                    }
                }
                
            } else {
                return {
                    success: false,
                    message: result.message
                }
            }
        } catch (error) {
            return this.handleError(error);
        }
    }

    async validatePackageAndAddList (package_barcode, location_id) {
        try {
            const response = await axios.post(
                `${this.baseUrl}/api/stock/validate-package`,
                {
                    jsonrpc: "2.0",
                    method: "call",
                    params: {
                        package_barcode: package_barcode,
                        location_id: location_id,
                    }
                },this.getConfig()
            )

            const result = response.data.result
            if (result.success){
                return {
                    success: true,
                    package: {
                        id: result.data.id,
                        name: result.data.name,
                    }
            }
            } else {
                return {
                    success: false,
                    message: result.message
                }
            }
        } catch (error) {
            return this.handleError(error);
        }
    }

    async internalTransfer(user_id, origin_location, packages, destination_location) {
        try {
            const response = await axios.post(
                `${this.baseUrl}/api/stock/internaltransfer`,
                {
                    jsonrpc: "2.0",
                    method: "call",
                    params: {
                        user_id: user_id,
                        origin_location: origin_location,
                        packages: packages,
                        destination_location: destination_location
                    }
                },this.getConfig()
            );
            const result = response.data.result

            if (result.success) {
                return {
                    success: true,
                    message: result.message
                }
            } else {
                return {
                    success: false,
                    message: result.message
                }
            }
        } catch (error) {
            return this.handleError(error);
        }
    }

    async detectBarcode(barcode, origin_location) {
        try {
            const response = await axios.post(
                `${this.baseUrl}/api/stock/detectbarcode`,
                {
                    jsonrpc: "2.0",
                    method: "call",
                    params: {
                        barcode: barcode,
                        origin_location: origin_location,
                    }
                },this.getConfig()
            );
            const result = response.data.result

            if (result.success) {
                return {
                    success: true,
                    type: result.type,
                    data: result.data
                }
            } else {
                return {
                    success: false,
                    message: result.message
                }
            }
        } catch (error) {
            return this.handleError(error);
        }
    }

    async packageInquiry(barcode) {
        try {
            const response = await axios.post(
                `${this.baseUrl}/api/stock/packageinquiry`,
                {
                    jsonrpc: "2.0",
                    method: "call",
                    params: {
                        barcode: barcode
                    }
                },this.getConfig()
            );
            const result = response.data.result

            if (result.success) {
                return {
                    success: true,
                    package: {
                        location_id: result.data.location_id,
                        state: result.data.state,
                        name: result.data.name,
                        products: result.data.product_name,
                        available_boxes: result.data.available_boxes,
                        total_weight_boxes: result.data.total_weight_boxes,
                        measurement: result.data.measurement
                    }
                }
            } else {
                return {
                    success: false,
                    message: result.message
                }
            }
        } catch (error) {
            return this.handleError(error);
        }
    }

    async loadInventoryIndicators(location_id) {
        try {
            const response = await axios.post(
                `${this.baseUrl}/api/stock/inventory-indicators`,
                {
                    jsonrpc: "2.0",
                    method: "call",
                    params: {
                        location_id: location_id,
                    }
                },this.getConfig()
            );

            const result = response.data.result

            if (result.success)
            {
                return {
                    success: true,
                    total: result.data.total_packages,
                    missing: result.data.missing_packages,
                    scanned: result.data.scanned_packages
                }   
            } else {
                return {
                    success: false,
                    message: result.message
                }
            }
        } catch (error) {
            return this.handleError(error);
        }
    }

    async packageToInventory(user_id, location_id, barcode) {
        try {
            const response = await axios.post(
                `${this.baseUrl}/api/stock/package-inventory`,
                {
                    jsonrpc: "2.0",
                    method: "call",
                    params: {
                        user_id: user_id,
                        location_id: location_id,
                        barcode: barcode
                    }
                },this.getConfig()
            );

            const result = response.data.result

            if (result.success)
            {
                return {
                    success: true
                }   
            } else {
                return {
                    success: false,
                    message: result.message
                }
            }
        } catch (error) {
            return this.handleError(error);
        }
    }

    async showPackages(location_id, type, page = 1) {
        try {
            const response = await axios.post(
                `${this.baseUrl}/api/stock/show-packages`,
                {
                    jsonrpc: "2.0",
                    method: "call",
                    params: {
                        location_id: location_id,
                        type: type, 
                        page: page
                    }
                },this.getConfig()
            );
            const result = response.data.result

            if (result.success)
            {
                return {
                    success: true,
                    title: result.data.title,
                    packages: result.data.packages,
                    total: result.data.total,
                    page: result.data.page,
                    pages: result.data.pages
                }   
            } else {
                return {
                    success: false,
                    message: result.message
                }
            }
        } catch (error) {
            return this.handleError(error);
        }
    }

    async actionCleanInventory(location_id, package_name) {
        try {
            const response = await axios.post(
                `${this.baseUrl}/api/stock/action-clean-inventory`,
                {
                    jsonrpc: "2.0",
                    method: "call",
                    params: {
                        location_id: location_id,
                        package_name: package_name
                    }
                },this.getConfig()
            );

            const result = response.data.result

            if (result.success)
            {
                return {
                    success: true
                }
            } else {
                return {
                    success: false,
                    message: result.message
                }
            }
        } catch (error) {
            return this.handleError(error);
        }
    }
    
};
const odooService = new OdooService();
export default odooService;